'use client';

import Link from 'next/link';
import { Linkedin, Twitter, ShieldAlert, Info } from 'lucide-react';
import Image from 'next/image';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { usePathname } from 'next/navigation';
import { getLocaleFromPathname, translations, toFrenchProfessionSlug, type Dictionary, type Locale } from '@/lib/i18n';
import { subscribeToNewsletter } from '@/app/actions/newsletter';
import { toast } from 'sonner';
import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import ReportProblemModal from '@/components/feedback/ReportProblemModal';

// Clean Footer Component
const getFooterColumns = (t: Dictionary, locale: Locale, toFrenchProfessionSlug: (slug: string) => string) => [
  {
    title: t.footer.directory,
    links: [
      { name: t.footer.links.aiForLegal, href: `/ai-for-${locale === 'fr' ? toFrenchProfessionSlug('legal') : 'legal'}` },
      { name: t.footer.links.aiForRealEstate, href: `/ai-for-${locale === 'fr' ? toFrenchProfessionSlug('real-estate') : 'real-estate'}` },
      { name: t.footer.links.aiForFinance, href: `/ai-for-${locale === 'fr' ? toFrenchProfessionSlug('finance') : 'finance'}` },
      { name: t.footer.links.aiForEducation, href: `/ai-for-${locale === 'fr' ? toFrenchProfessionSlug('education') : 'education'}` },
      { name: t.footer.links.popularTools, href: '/tools' },
    ],
  },
  {
    title: t.footer.platform,
    links: [
      { name: t.footer.links.howWeReview, href: '/about' },
      { name: t.footer.links.latestInsights, href: '/insights' },
      { name: t.footer.links.submitTool, href: '/submit-tool' },
      { name: t.footer.links.newsletter, href: '/newsletter' },
    ],
  },
  {
    title: t.footer.legal,
    links: [
      { name: t.footer.links.privacy, href: '/privacy' },
      { name: t.footer.links.terms, href: '/terms' },
      { name: t.footer.links.disclaimer, href: '/disclaimer' },
      { name: t.footer.links.cookiePolicy, href: '/cookies' },
    ],
  },
  {
    title: t.footer.help,
    links: [
      { name: t.footer.links.contactUs, href: '/contact' },
      { name: t.footer.links.helpCenter, href: '/help' },
      { name: t.footer.links.reportProblem, href: '#report-problem', isReport: true },
    ],
  },
];

export default function Footer() {
  const pathname = usePathname();
  const locale = getLocaleFromPathname(pathname);
  const t = translations[locale];
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);

  const unlocalizedRoutes = ['/dashboard', '/login', '/register', '/admin'];
  const localizeHref = (href: string) => {
    if (unlocalizedRoutes.some(route => href.startsWith(route))) return href;
    return locale === 'fr' ? `/fr${href}` : `/en${href}`;
  };

  async function handleNewsletterSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setIsSubmitting(true);
    const formData = new FormData(e.currentTarget);
    const result = await subscribeToNewsletter(formData);
    
    if (result.success) {
      toast.success(result.message);
      (e.target as HTMLFormElement).reset();
    } else {
      toast.error(result.error || 'Failed to subscribe');
    }
    setIsSubmitting(false);
  }

  return (
    <footer className="footer-surface">
      <div className="relative z-10 max-w-7xl mx-auto px-4 py-14 sm:px-6 lg:px-8">
        <div className="grid gap-12 lg:grid-cols-[1.1fr_1.9fr]">
          <div className="flex min-w-0 flex-col gap-6">
            <div className="flex min-w-0 flex-col items-start">
              <Link href={localizeHref('/')} className="flex items-center group mb-3" aria-label="GuideToReviews Home">
                <Image
                  src="/logo.svg"
                  alt=""
                  width={28}
                  height={28}
                  className="mr-2 transition-transform group-hover:scale-110"
                  unoptimized
                />
                <span className="font-bold text-lg text-foreground tracking-tight">GuideToReviews</span>
              </Link>
              <p className="break-words text-sm text-muted-foreground leading-relaxed">
                {t.footer.tagline}
              </p>
            </div>

            <form onSubmit={handleNewsletterSubmit} className="flex flex-col gap-3">
              <label htmlFor="footer-email" className="text-sm font-semibold text-foreground sr-only">
                {t.footer.stayInLoop}
              </label>
              <div className="flex flex-col gap-2 sm:flex-row">
                <Input
                  id="footer-email"
                  name="email"
                  type="email"
                  placeholder="Enter your email"
                  autoComplete="email"
                  className="min-w-0 border-primary/35 bg-card/30 text-foreground placeholder:text-muted-foreground"
                  aria-label="Email address for newsletter"
                  required
                />
                <Button 
                  type="submit" 
                  disabled={isSubmitting}
                  className="sm:w-auto bg-primary hover:bg-primary/90 text-primary-foreground font-bold transition-colors min-w-[100px]"
                >
                  {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : t.footer.subscribe}
                </Button>
              </div>
              <span className="text-xs text-muted-foreground/80">
                {t.footer.stayInLoop}
              </span>
            </form>

            <div className="flex flex-wrap items-center gap-3">
              <Link
                href="https://x.com/PlanettoolsOrg"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Follow GuideToReviews on X/Twitter (opens in new tab)"
                className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-primary/20 bg-card/50 text-muted-foreground transition-all duration-200 hover:scale-110 hover:border-primary/40 hover:text-primary"
              >
                <Twitter className="h-5 w-5" />
              </Link>
              <Link
                href="https://www.linkedin.com/company/guidetoreviews/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Follow GuideToReviews on LinkedIn (opens in new tab)"
                className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-primary/20 bg-card/50 text-muted-foreground transition-all duration-200 hover:scale-110 hover:border-primary/40 hover:text-primary"
              >
                <Linkedin className="h-5 w-5" />
              </Link>
            </div>
          </div>

          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {getFooterColumns(t, locale, toFrenchProfessionSlug).map((column) => (
              <div key={column.title} className="flex min-w-0 flex-col gap-3">
                <p className="text-sm font-semibold text-foreground">{column.title}</p>
                <div className="flex flex-col gap-2">
                  {column.links.map((link: any) => (
                    link.isReport ? (
                      <button
                        key={link.name}
                        type="button"
                        onClick={() => setIsReportModalOpen(true)}
                        className="text-left break-words text-sm text-muted-foreground transition-colors duration-200 hover:text-primary hover:underline cursor-pointer"
                      >
                        {link.name}
                      </button>
                    ) : (
                      <Link
                        key={link.name}
                        href={localizeHref(link.href)}
                        className="break-words text-sm text-muted-foreground transition-colors duration-200 hover:text-primary hover:underline"
                      >
                        {link.name}
                      </Link>
                    )
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <hr className="my-8 border-primary/10" />

        <div className="flex flex-col items-center gap-3 text-center">
          <p className="flex max-w-3xl flex-wrap items-center justify-center gap-2 break-words text-xs text-muted-foreground/60">
            <ShieldAlert className="h-3.5 w-3.5 text-primary/40" aria-hidden="true" />
            <span className="inline-flex flex-wrap items-center justify-center gap-2 italic">
              <strong>{t.footer.noticePrefix}</strong> {t.footer.noticeText}
              <span
                className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-primary/20 bg-card/40 text-primary/80"
                role="img"
                aria-label="More legal information"
                title={t.footer.noticeText}
              >
                <Info className="h-3 w-3" aria-hidden="true" />
              </span>
            </span>
          </p>

          <div className="mt-2 space-y-1">
            <p className="text-xs text-muted-foreground m-0">
              {t.footer.copyright}
            </p>
            <p className="text-[10px] text-muted-foreground/50 m-0 uppercase tracking-widest font-medium italic">
              {t.footer.madeWithLove}
            </p>
          </div>
        </div>
      </div>

      <ReportProblemModal
        isOpen={isReportModalOpen}
        onClose={() => setIsReportModalOpen(false)}
      />
    </footer>
  );
}
